import type { SttRealtimeSession } from '@hub/contracts'
import { createPcmMic, PCM_SAMPLE_RATE, type PcmMic } from './pcm-mic'
import { DictationError, type DictationHandlers, type DictationTransport } from './transport'

/**
 * Soniox realtime over a raw WebSocket (docs: stt/api-reference/websocket-api).
 * The temporary key rides the WebSocket protocols list as
 * `['soniox-api-key', <key>]` (docs: guides/websocket-authentication — a
 * browser cannot set headers; `api_key` in the config message is deprecated
 * and sending both is a 400). Only `snx_temp_` keys work there. A bad key
 * still opens the socket and arrives as an error frame, then a close.
 * First message = JSON config (no key); then binary PCM frames;
 * `{"type":"finalize"}` to force finals, an empty TEXT frame to end the audio
 * (verified live 2026-09-19: an empty binary frame is ignored and the socket
 * idles into a 408 — the docs' own example sends `""`).
 *
 * Token semantics (docs: stt/rt/real-time-transcription): every response
 * carries the tokens finalized since the last response (`is_final: true`,
 * delivered once — appended) plus the CURRENT non-final tail (a rolling
 * window, re-sent whole — replaced). Token text carries its own leading
 * whitespace, so joining is `''`. `<end>` (endpoint) and `<fin>` (finalize
 * done) are markers, never text.
 */

export const SONIOX_AUDIO_FORMAT = 'pcm_s16le'
/** Send audio in ≥100 ms chunks: 20 ms worklet frames would be 50 messages/s. */
const CHUNK_SAMPLES = (PCM_SAMPLE_RATE * 100) / 1000
/** Soniox drops an idle socket after 20 s; keepalive well inside that. */
const KEEPALIVE_MS = 10_000
const MARKER_TOKENS = new Set(['<end>', '<fin>'])
/** End-of-audio is an empty *text* frame — see the header comment. */
export const END_OF_AUDIO = ''

export interface SonioxToken {
  text: string
  start_ms?: number
  end_ms?: number
  confidence?: number
  is_final: boolean
  speaker?: string
  language?: string
}

export interface SonioxResult {
  tokens?: SonioxToken[]
  final_audio_proc_ms?: number
  total_audio_proc_ms?: number
  finished?: boolean
  error_code?: number | string
  error_type?: string
  error_message?: string
}

export function isMarkerToken(token: Pick<SonioxToken, 'text'>): boolean {
  return MARKER_TOKENS.has(token.text.trim())
}

/** The one place token text becomes a string (PLAN §6 "token text joining"). */
export function joinTokens(tokens: Array<Pick<SonioxToken, 'text'>>): string {
  return tokens.map((t) => t.text).join('')
}

/** Minimal socket surface so tests can inject a fake. */
export interface SocketLike {
  readyState: number
  binaryType: string
  onopen: ((ev: unknown) => void) | null
  onmessage: ((ev: { data: unknown }) => void) | null
  onerror: ((ev: unknown) => void) | null
  onclose: ((ev: unknown) => void) | null
  send(data: string | ArrayBuffer | ArrayBufferView): void
  close(): void
}
export type SocketFactory = (url: string, protocols: string[]) => SocketLike
export const SONIOX_AUTH_PROTOCOL = 'soniox-api-key'
const OPEN = 1

export function createSonioxTransport(
  deps: { socket?: SocketFactory; mic?: PcmMic } = {}
): DictationTransport {
  const makeSocket: SocketFactory =
    deps.socket ?? ((url, protocols) => new WebSocket(url, protocols) as unknown as SocketLike)
  const mic = deps.mic ?? createPcmMic()

  let ws: SocketLike | null = null
  let stream: MediaStream | null = null
  let handlers: DictationHandlers | null = null
  let committed = ''
  let closed = false
  /** Pending PCM, coalesced into ≥100 ms sends. */
  let pending: Int16Array[] = []
  let pendingSamples = 0
  let lastSendAt = 0
  let keepalive: ReturnType<typeof setInterval> | null = null
  /** Resolved by the `<fin>` marker / `finished` — see flush(). */
  let onFin: (() => void) | null = null
  let onFinished: (() => void) | null = null

  const send = (data: string | ArrayBuffer) => {
    if (ws && ws.readyState === OPEN) ws.send(data)
  }

  const flushPending = () => {
    if (pendingSamples === 0) return
    const out = new Int16Array(pendingSamples)
    let offset = 0
    for (const part of pending) {
      out.set(part, offset)
      offset += part.length
    }
    pending = []
    pendingSamples = 0
    lastSendAt = Date.now()
    send(out.buffer)
  }

  const onFrame = (pcm: Int16Array) => {
    if (closed) return
    pending.push(pcm)
    pendingSamples += pcm.length
    if (pendingSamples >= CHUNK_SAMPLES) flushPending()
  }

  const fail = (message: string) => {
    if (closed) return
    handlers?.onError(new DictationError(message, 'failed'))
    close()
  }

  const onMessage = (raw: unknown) => {
    if (typeof raw !== 'string') return
    let result: SonioxResult
    try {
      result = JSON.parse(raw) as SonioxResult
    } catch {
      return
    }
    if (result.error_code !== undefined || result.error_type || result.error_message) {
      console.error(
        `[dictation] soniox error ${result.error_code ?? ''} ${result.error_type ?? ''}: ${result.error_message ?? ''}`
      )
      fail(result.error_message ?? 'soniox error')
      return
    }
    const tokens = result.tokens ?? []
    const finals = tokens.filter((t) => t.is_final && !isMarkerToken(t))
    const nonFinals = tokens.filter((t) => !t.is_final && !isMarkerToken(t))
    // The accumulator keeps every token verbatim (sub-word pieces glue with
    // their own whitespace); what the hook sees is trimmed, because the
    // composer joins base + committed + partial with single spaces itself.
    if (finals.length > 0) {
      committed += joinTokens(finals)
      handlers?.onCommitted(committed.trim())
    }
    handlers?.onPartial(joinTokens(nonFinals).trim())
    if (tokens.some((t) => t.is_final && t.text.trim() === '<fin>')) onFin?.()
    if (result.finished) onFinished?.()
  }

  const close = () => {
    if (closed) return
    closed = true
    if (keepalive) clearInterval(keepalive)
    keepalive = null
    pending = []
    pendingSamples = 0
    void mic.stop()
    // The mic indicator goes off with the session, not with the page.
    for (const track of stream?.getTracks() ?? []) track.stop()
    stream = null
    const socket = ws
    ws = null
    try {
      socket?.close()
    } catch {
      // already closed
    }
  }

  return {
    async start(session, micStream, h) {
      if (session.provider !== 'soniox') throw new DictationError('wrong provider', 'failed')
      handlers = h
      committed = ''
      stream = micStream
      closed = false
      const socket = makeSocket(session.url, [SONIOX_AUTH_PROTOCOL, session.apiKey])
      socket.binaryType = 'arraybuffer'
      ws = socket
      await new Promise<void>((resolve, reject) => {
        let settled = false
        socket.onopen = () => {
          socket.send(
            JSON.stringify({
              model: session.model,
              audio_format: SONIOX_AUDIO_FORMAT,
              sample_rate: PCM_SAMPLE_RATE,
              num_channels: 1,
              language_hints: session.languageHints,
              enable_endpoint_detection: true,
              ...(session.context ? { context: session.context } : {}),
            })
          )
          settled = true
          resolve()
        }
        socket.onmessage = (ev) => onMessage(ev.data)
        socket.onerror = () => {
          if (!settled) {
            settled = true
            reject(new DictationError('socket error', 'failed'))
            return
          }
          fail('socket error')
        }
        socket.onclose = () => {
          const wasOurs = ws === socket
          if (!settled) {
            settled = true
            reject(new DictationError('socket closed', 'failed'))
          }
          if (wasOurs) close()
          h.onClose()
        }
      })
      await mic.start(micStream, onFrame)
      lastSendAt = Date.now()
      keepalive = setInterval(() => {
        if (Date.now() - lastSendAt >= KEEPALIVE_MS) {
          lastSendAt = Date.now()
          send(JSON.stringify({ type: 'keepalive' }))
        }
      }, 1_000)
    },

    async flush(timeoutMs) {
      if (closed || !ws) return
      // Stop feeding first so `finalize` really is the last audio.
      await mic.stop()
      flushPending()
      await new Promise<void>((resolve) => {
        let done = false
        const finish = () => {
          if (done) return
          done = true
          onFin = null
          resolve()
        }
        onFin = finish
        send(JSON.stringify({ type: 'finalize' }))
        setTimeout(finish, timeoutMs)
      })
      if (closed || !ws) return
      // End of audio (empty text frame): the server answers `finished: true`
      // and closes.
      await new Promise<void>((resolve) => {
        let done = false
        const finish = () => {
          if (done) return
          done = true
          onFinished = null
          resolve()
        }
        onFinished = finish
        send(END_OF_AUDIO)
        setTimeout(finish, Math.min(timeoutMs, 500))
      })
      close()
    },

    close,
  }
}
